// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import {
  LayoutDashboard, FileText, Key, Webhook, Database,
  ScrollText, CreditCard, Settings, User, Bell, Shield,
  Users, BarChart2, HardDrive, Activity, Upload, Trash2,
  Download, RefreshCw, Plus, Lock, Globe,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ROUTES } from '@/constants/routes'

export interface SearchEntry {
  id: string
  text: string
  description?: string
  page: string
  section?: string
  to: string
  icon: LucideIcon
  keywords?: string[]
}

export const SEARCH_INDEX: SearchEntry[] = [
  // ── Dashboard ──────────────────────────────────────────────
  {
    id: 'dashboard',
    text: 'Dashboard',
    description: 'Overview of your workspace activity',
    page: 'Dashboard',
    to: ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    keywords: ['home', 'overview', 'stats', 'analytics'],
  },
  {
    id: 'dashboard-usage',
    text: 'Usage statistics',
    description: 'Documents, API calls, storage used this month',
    page: 'Dashboard',
    section: 'Stats',
    to: ROUTES.DASHBOARD,
    icon: BarChart2,
    keywords: ['usage', 'stats', 'metrics', 'quota', 'limit'],
  },
  {
    id: 'dashboard-recent-docs',
    text: 'Recent documents',
    description: 'Last uploaded SDF documents',
    page: 'Dashboard',
    section: 'Recent documents',
    to: ROUTES.DASHBOARD,
    icon: FileText,
    keywords: ['recent', 'documents', 'uploaded'],
  },
  {
    id: 'dashboard-storage',
    text: 'Storage used',
    description: 'How much storage your documents are using',
    page: 'Dashboard',
    section: 'Stats',
    to: ROUTES.DASHBOARD,
    icon: HardDrive,
    keywords: ['storage', 'disk', 'gb', 'space'],
  },
  {
    id: 'dashboard-api-calls',
    text: 'API calls this month',
    description: 'Total API requests in the current billing period',
    page: 'Dashboard',
    section: 'Stats',
    to: ROUTES.DASHBOARD,
    icon: Activity,
    keywords: ['api calls', 'requests', 'usage', 'rate limit'],
  },

  // ── Documents ──────────────────────────────────────────────
  {
    id: 'documents',
    text: 'Documents',
    description: 'Upload and manage SDF documents',
    page: 'Documents',
    to: ROUTES.DOCUMENTS,
    icon: FileText,
    keywords: ['sdf', 'files', 'upload', 'documents'],
  },
  {
    id: 'documents-upload',
    text: 'Upload document',
    description: 'Upload a new SDF file',
    page: 'Documents',
    section: 'Actions',
    to: ROUTES.DOCUMENTS,
    icon: Upload,
    keywords: ['upload', 'new', 'add', 'import', 'sdf file'],
  },
  {
    id: 'documents-delete',
    text: 'Delete document',
    description: 'Remove a document from your workspace',
    page: 'Documents',
    section: 'Actions',
    to: ROUTES.DOCUMENTS,
    icon: Trash2,
    keywords: ['delete', 'remove', 'trash'],
  },
  {
    id: 'documents-status-valid',
    text: 'Valid documents',
    description: 'Documents that passed SDF validation',
    page: 'Documents',
    section: 'Status',
    to: ROUTES.DOCUMENTS,
    icon: FileText,
    keywords: ['valid', 'verified', 'passed'],
  },
  {
    id: 'documents-status-processing',
    text: 'Processing documents',
    description: 'Documents currently being validated',
    page: 'Documents',
    section: 'Status',
    to: ROUTES.DOCUMENTS,
    icon: RefreshCw,
    keywords: ['processing', 'pending', 'validating'],
  },
  {
    id: 'documents-status-invalid',
    text: 'Invalid documents',
    description: 'Documents that failed validation',
    page: 'Documents',
    section: 'Status',
    to: ROUTES.DOCUMENTS,
    icon: FileText,
    keywords: ['invalid', 'failed', 'error', 'rejected'],
  },

  // ── API Keys ───────────────────────────────────────────────
  {
    id: 'api-keys',
    text: 'API Keys',
    description: 'Create and manage API keys for authentication',
    page: 'API Keys',
    to: ROUTES.API_KEYS,
    icon: Key,
    keywords: ['api key', 'token', 'secret', 'auth', 'authentication'],
  },
  {
    id: 'api-keys-create',
    text: 'Create API key',
    description: 'Generate a new API key',
    page: 'API Keys',
    section: 'Actions',
    to: ROUTES.API_KEYS,
    icon: Plus,
    keywords: ['create', 'generate', 'new api key', 'token'],
  },
  {
    id: 'api-keys-revoke',
    text: 'Revoke API key',
    description: 'Permanently disable an API key',
    page: 'API Keys',
    section: 'Actions',
    to: ROUTES.API_KEYS,
    icon: Trash2,
    keywords: ['revoke', 'delete', 'disable', 'remove api key'],
  },
  {
    id: 'api-keys-scopes',
    text: 'API key scopes',
    description: 'documents:read, documents:write, webhooks:manage, schemas:read',
    page: 'API Keys',
    section: 'Permissions',
    to: ROUTES.API_KEYS,
    icon: Lock,
    keywords: ['scope', 'permission', 'read', 'write', 'manage', 'access'],
  },
  {
    id: 'api-keys-last-used',
    text: 'Last used',
    description: 'When the API key was last used to make a request',
    page: 'API Keys',
    section: 'Table',
    to: ROUTES.API_KEYS,
    icon: Activity,
    keywords: ['last used', 'activity', 'recent'],
  },

  // ── Webhooks ───────────────────────────────────────────────
  {
    id: 'webhooks',
    text: 'Webhooks',
    description: 'Receive real-time event notifications',
    page: 'Webhooks',
    to: ROUTES.WEBHOOKS,
    icon: Webhook,
    keywords: ['webhook', 'event', 'notification', 'callback', 'endpoint'],
  },
  {
    id: 'webhooks-add',
    text: 'Add webhook endpoint',
    description: 'Register a URL to receive event payloads',
    page: 'Webhooks',
    section: 'Actions',
    to: ROUTES.WEBHOOKS,
    icon: Plus,
    keywords: ['add', 'create', 'endpoint', 'url'],
  },
  {
    id: 'webhooks-events-document',
    text: 'Document events',
    description: 'document.uploaded, document.processed, document.verified, document.deleted',
    page: 'Webhooks',
    section: 'Events',
    to: ROUTES.WEBHOOKS,
    icon: FileText,
    keywords: ['document events', 'uploaded', 'processed', 'verified', 'deleted'],
  },
  {
    id: 'webhooks-events-api-key',
    text: 'API key events',
    description: 'api_key.created, api_key.revoked',
    page: 'Webhooks',
    section: 'Events',
    to: ROUTES.WEBHOOKS,
    icon: Key,
    keywords: ['api key events', 'created', 'revoked'],
  },
  {
    id: 'webhooks-events-billing',
    text: 'Billing events',
    description: 'billing.invoice_created, billing.payment_failed',
    page: 'Webhooks',
    section: 'Events',
    to: ROUTES.WEBHOOKS,
    icon: CreditCard,
    keywords: ['billing events', 'invoice', 'payment', 'failed'],
  },
  {
    id: 'webhooks-toggle',
    text: 'Enable / Pause webhook',
    description: 'Temporarily pause event delivery without deleting the endpoint',
    page: 'Webhooks',
    section: 'Actions',
    to: ROUTES.WEBHOOKS,
    icon: Globe,
    keywords: ['enable', 'disable', 'pause', 'toggle', 'active', 'inactive'],
  },

  // ── Schema Registry ────────────────────────────────────────
  {
    id: 'schema-registry',
    text: 'Schema Registry',
    description: 'Manage and version SDF document schemas',
    page: 'Schema Registry',
    to: ROUTES.SCHEMA_REGISTRY,
    icon: Database,
    keywords: ['schema', 'registry', 'version', 'sdf structure'],
  },
  {
    id: 'schema-registry-version',
    text: 'Schema versions',
    description: 'Track schema versions like v1.0.0, v2.0.0',
    page: 'Schema Registry',
    section: 'Versions',
    to: ROUTES.SCHEMA_REGISTRY,
    icon: Database,
    keywords: ['version', 'v1', 'v2', 'draft', 'active'],
  },

  // ── Audit Log ──────────────────────────────────────────────
  {
    id: 'audit-log',
    text: 'Audit Log',
    description: 'Track all actions across your workspace',
    page: 'Audit Log',
    to: ROUTES.AUDIT_LOG,
    icon: ScrollText,
    keywords: ['audit', 'log', 'history', 'activity', 'events', 'trail'],
  },
  {
    id: 'audit-log-actions',
    text: 'Audit actions',
    description: 'document.uploaded, api_key.created, api_key.revoked and more',
    page: 'Audit Log',
    section: 'Actions',
    to: ROUTES.AUDIT_LOG,
    icon: ScrollText,
    keywords: ['action', 'event type', 'who did what'],
  },

  // ── Billing ────────────────────────────────────────────────
  {
    id: 'billing',
    text: 'Billing',
    description: 'Manage your plan, usage and invoices',
    page: 'Billing',
    to: ROUTES.BILLING,
    icon: CreditCard,
    keywords: ['billing', 'plan', 'payment', 'invoice', 'subscription'],
  },
  {
    id: 'billing-plan',
    text: 'Current plan',
    description: 'Your active subscription plan and price',
    page: 'Billing',
    section: 'Plan',
    to: ROUTES.BILLING,
    icon: CreditCard,
    keywords: ['plan', 'starter', 'pro', 'enterprise', 'subscription'],
  },
  {
    id: 'billing-upgrade',
    text: 'Upgrade plan',
    description: 'Switch to a higher tier for more usage',
    page: 'Billing',
    section: 'Plan',
    to: ROUTES.BILLING,
    icon: CreditCard,
    keywords: ['upgrade', 'pro', 'enterprise', 'tier', 'switch plan'],
  },
  {
    id: 'billing-usage',
    text: 'Usage meters',
    description: 'Documents, API calls and storage usage this billing period',
    page: 'Billing',
    section: 'Usage',
    to: ROUTES.BILLING,
    icon: BarChart2,
    keywords: ['usage', 'quota', 'limit', 'meter', 'period'],
  },
  {
    id: 'billing-invoices',
    text: 'Invoice history',
    description: 'Download past invoices',
    page: 'Billing',
    section: 'Invoices',
    to: ROUTES.BILLING,
    icon: Download,
    keywords: ['invoice', 'receipt', 'download', 'history', 'paid'],
  },

  // ── Settings ───────────────────────────────────────────────
  {
    id: 'settings',
    text: 'Settings',
    description: 'Workspace and account settings',
    page: 'Settings',
    to: ROUTES.SETTINGS,
    icon: Settings,
    keywords: ['settings', 'preferences', 'configuration'],
  },
  {
    id: 'settings-profile',
    text: 'Profile',
    description: 'Update your name, email and avatar',
    page: 'Settings',
    section: 'Profile',
    to: ROUTES.SETTINGS_PROFILE,
    icon: User,
    keywords: ['profile', 'name', 'email', 'avatar', 'account'],
  },
  {
    id: 'settings-profile-name',
    text: 'Full name',
    description: 'Change your display name',
    page: 'Settings',
    section: 'Profile',
    to: ROUTES.SETTINGS_PROFILE,
    icon: User,
    keywords: ['name', 'full name', 'display name'],
  },
  {
    id: 'settings-profile-email',
    text: 'Email address',
    description: 'Your login email — contact support to change',
    page: 'Settings',
    section: 'Profile',
    to: ROUTES.SETTINGS_PROFILE,
    icon: User,
    keywords: ['email', 'login email', 'contact'],
  },
  {
    id: 'settings-password',
    text: 'Change password',
    description: 'Update your account password',
    page: 'Settings',
    section: 'Profile › Security',
    to: ROUTES.SETTINGS_PROFILE,
    icon: Lock,
    keywords: ['password', 'change password', 'security', 'credentials'],
  },
  {
    id: 'settings-organization',
    text: 'Organization',
    description: 'Manage your organization details',
    page: 'Settings',
    section: 'Organization',
    to: ROUTES.SETTINGS_ORGANIZATION,
    icon: Settings,
    keywords: ['organization', 'org', 'company', 'workspace'],
  },
  {
    id: 'settings-team',
    text: 'Team members',
    description: 'Invite and manage team members',
    page: 'Settings',
    section: 'Team',
    to: ROUTES.SETTINGS_TEAM,
    icon: Users,
    keywords: ['team', 'members', 'invite', 'collaborators', 'roles'],
  },
  {
    id: 'settings-security',
    text: 'Security',
    description: 'Two-factor authentication and session management',
    page: 'Settings',
    section: 'Security',
    to: ROUTES.SETTINGS_SECURITY,
    icon: Shield,
    keywords: ['security', '2fa', 'two factor', 'session', 'mfa'],
  },
  {
    id: 'notifications-inbox',
    text: 'Notification inbox',
    description: 'Read in-app messages and platform notices',
    page: 'App',
    section: 'Notifications',
    to: ROUTES.NOTIFICATIONS,
    icon: Bell,
    keywords: ['notifications', 'inbox', 'bell', 'alerts', 'messages', 'activity'],
  },
  {
    id: 'settings-notifications',
    text: 'Notification preferences',
    description: 'Email and in-app category toggles',
    page: 'Settings',
    section: 'Notifications',
    to: ROUTES.SETTINGS_NOTIFICATIONS,
    icon: Bell,
    keywords: ['notifications', 'email', 'preferences', 'settings', 'toggle', 'categories'],
  },

  // ── Auth ───────────────────────────────────────────────────
  {
    id: 'auth-logout',
    text: 'Sign out',
    description: 'Log out of your account',
    page: 'Account',
    to: ROUTES.LOGIN,
    icon: User,
    keywords: ['sign out', 'logout', 'log out'],
  },
]

export function searchIndex(query: string): SearchEntry[] {
  if (!query.trim()) return []
  const q = query.toLowerCase().trim()
  return SEARCH_INDEX.filter((entry) => {
    const haystack = [
      entry.text,
      entry.description ?? '',
      entry.page,
      entry.section ?? '',
      ...(entry.keywords ?? []),
    ].join(' ').toLowerCase()
    return haystack.includes(q)
  })
}
